import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    IconButton,
    Typography,
    Card,
    CardContent,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ButtonBase,
    Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import BadgeIcon from '@mui/icons-material/Badge';
import BusinessIcon from '@mui/icons-material/Business';
import StarIcon from '@mui/icons-material/Star';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { useNavigate } from 'react-router-dom';
import useAuthRedirect from '../../userAutoRedirect';
import { getDivisionName } from './division';

const RoundedButton = styled(IconButton)(({ theme }) => ({
    width: 40,
    height: 40,
    borderRadius: '50%',
    backgroundColor: '#1A73C6',
    color: theme.palette.common.white,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: theme.shadows[2],
    position: 'fixed',
    top: 20,
    right: 25,
    zIndex: 1000,
    [theme.breakpoints.down('sm')]: {
        width: 35,
        height: 35,
        top: 13,
        right: 15,
    },
    [theme.breakpoints.between('sm', 'lg')]: {
        width: 35,
        height: 35,
        top: 13,
        right: 20,
    },
    '&:active, &:focus, &:hover': {
        backgroundColor: '#6EA7D4',
    },
}));

const StyledCard = styled(Card)(({ theme }) => ({
    position: 'absolute',
    top: 65,
    right: 25,
    minWidth: 210,
    maxWidth: 210,
    zIndex: 1000,
    [theme.breakpoints.down('sm')]: {
        minWidth: 200,
        maxWidth: 200,
        Padding: 0,
        top: 50,
        right: 15,
    },
    [theme.breakpoints.between('sm', 'lg')]: {
        minWidth: 250,
        maxWidth: 250,
        Padding: 0,
        top: 50,
        right: 20,
    },
}));

const UserDetailsList = styled(ListItemText)(({ theme }) => ({
    '& .MuiTypography-root': {
        // whiteSpace: 'nowrap',
        // overflow: 'hidden',
        // textOverflow: 'ellipsis',
        fontSize: '0.875rem',
        [theme.breakpoints.down('sm')]: {
            fontSize: '0.75rem',
        },
    },
}));

const UserListItemIcon = styled(ListItemIcon)(({ theme }) => ({
    minWidth: '25px',
    alignSelf: 'flex-start',
    paddingTop: theme.spacing(0.4),
    '& .MuiSvgIcon-root': {
        fontSize: '1.2rem',
    },
    [theme.breakpoints.down('sm')]: {
        '& .MuiSvgIcon-root': {
            fontSize: '1rem',
        },
        minWidth: '20px',
        paddingTop: theme.spacing(0.55),
    },
}));

const UserList = styled(ListItem)(({ theme }) => ({
    paddingLeft: 0,
    paddingTop: 0,
    paddingBottom: 0,
    paddingRight: 0,
}));

const checkSession = () => {
    return !!sessionStorage.getItem('userDetails');
};

const convertToTitleCase = (name: string): string => {
    return name
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

const useClickOutside = (handler: () => void) => {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (ref.current && !ref.current.contains(event.target as Node)) {
                handler();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [handler]);

    return ref;
};

const ProfileComponent: React.FC = () => {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    const handleClick = () => {
        setOpen(!open);
    };

    const handleLogout = () => {
        sessionStorage.clear();
        navigate('/');
    };

    const clickOutsideRef = useClickOutside(() => setOpen(false));

    const sessionExists = checkSession();
    useAuthRedirect(sessionExists);
    if (!sessionExists) {
        return null;
    }

    const userDetailsString = sessionStorage.getItem('userDetails');
    const userDetailsJson = userDetailsString ? JSON.parse(userDetailsString) : null;
    const userDetailsObject = userDetailsJson ? userDetailsJson : {};

    function getInitials(name: string): string {
        const words = name.trim().split(' ');

        let initials = '';

        if (words.length === 2) {
            initials = words[0].charAt(0) + words[1].charAt(0);
        } else if (words.length >= 3) {
            initials = words[0].charAt(0) + words[2].charAt(0);
            // initials = words[0].charAt(0) + words[words.length - 1].charAt(0);
        } else if (words.length === 1) {
            initials = words[0].charAt(0);
        }

        return initials.toUpperCase();
    }

    const name = userDetailsObject['Name'];
    const initials = getInitials(name);

    return (
        <Box>
            {!open && (
                <RoundedButton onClick={handleClick}>
                    <Typography variant="h6">{initials}</Typography>
                </RoundedButton>
            )}
            {open && (
                <>
                    <RoundedButton>
                        <Typography variant="h6">{initials}</Typography>
                    </RoundedButton>
                    <StyledCard ref={clickOutsideRef} sx={{ padding: 0 }} onClick={(e) => e.stopPropagation()}>
                        <CardContent sx={{ paddingBottom: 0, '&:last-of-type': { paddingBottom: 1 } }}>
                            <List sx={{ padding: 0 }} >
                                <UserList>
                                    <UserListItemIcon>
                                        <AccountCircleIcon sx={{ color: "#1A73C6" }} />
                                    </UserListItemIcon>
                                    <UserDetailsList primary={convertToTitleCase(userDetailsObject['Name'])} />
                                </UserList>
                                <UserList>
                                    <UserListItemIcon>
                                        <BadgeIcon sx={{ color: "#1A73C6" }} />
                                    </UserListItemIcon >
                                    <UserDetailsList primary={`Emp ID: ${userDetailsObject['emp_No']}`} />
                                </UserList>
                                <UserList>
                                    <UserListItemIcon>
                                        <BusinessIcon sx={{ color: "#1A73C6" }} />
                                    </UserListItemIcon>
                                    <UserDetailsList primary={getDivisionName(userDetailsObject['Division'])} />
                                </UserList>
                                <UserList>
                                    <UserListItemIcon>
                                        <StarIcon sx={{ color: "#1A73C6" }} />
                                    </UserListItemIcon>
                                    <UserDetailsList primary={convertToTitleCase(userDetailsObject['Designation'])} />
                                </UserList>
                                <UserList>
                                    <UserListItemIcon>
                                        <LocationOnIcon sx={{ color: "#1A73C6" }} />
                                    </UserListItemIcon>
                                    <UserDetailsList primary={convertToTitleCase(userDetailsObject['Location'])} />
                                </UserList>
                                <Divider sx={{
                                    height: '3px', backgroundColor: '#ECEDF2', borderBottomWidth: 0, width: '80%', margin: '0 auto', marginTop: '10px', marginBottom: '10px',
                                }} />
                                <UserList>
                                    <ButtonBase onClick={handleLogout}>
                                        <UserListItemIcon>
                                            <ExitToAppIcon sx={{ color: '#1A73C6' }} />
                                        </UserListItemIcon>
                                        <UserDetailsList primary="Logout" />
                                    </ButtonBase>
                                </UserList>
                            </List>
                        </CardContent>
                    </StyledCard>
                </>
            )}
        </Box>
    );
};

export default ProfileComponent;
